import { defaultsDeep } from 'lodash'
import Axios from 'axios'
import { DefaultAxiosRequestSettings } from './conf'

import type { AxiosRequestConfig, AxiosInstance } from 'axios'
import type * as Types from './types'

/** 错误原因 */
export interface RejectedReason extends Partial<Error> {
  origin?: any
}

/** 是否为错误原因 */
export const isRejectedReason = (exception?: RejectedReason) => {
  return exception instanceof Error && 'origin' in exception
}

/** 创建错误原因  */
export const createRejectedReason = (message: string, exception?: RejectedReason): RejectedReason => {
  if (isRejectedReason(exception)) {
    return exception
  }

  const reason: RejectedReason = new Error(message)
  reason.message = message
  reason.name = exception?.name
  reason.stack = exception?.stack
  reason.origin = exception
  return reason
}

/** 是否为取消或者中断 */
export const isCancelOrAbsorted = (rejection: RejectedReason) => {
  if (Axios.isCancel(rejection)) {
    return true
  }

  if (-1 !== rejection?.message?.indexOf('Request aborted')) {
    return true
  }

  if (rejection.origin) {
    return isCancelOrAbsorted(rejection.origin)
  }

  return false
}

/**
 * 创建 Axios 实例
 * @param config 请求配置
 *
 * @description
 * 这里主要为了统一默认配置, 例如默认拦截器等
 */
export function createAxios(config: AxiosRequestConfig = DefaultAxiosRequestSettings): AxiosInstance {
  const finallyConfig: AxiosRequestConfig = defaultsDeep({}, config, DefaultAxiosRequestSettings)
  const request: AxiosInstance = Axios.create(finallyConfig)

  request.interceptors.response.use(
    (response) => {
      const { status, config = {} } = response || {}
      const method = (config.method || 'GET').toLocaleUpperCase()

      /**
       * hidden body when method euqal DELETE
       * https://stackoverflow.com/questions/299628/is-an-entity-body-allowed-for-an-http-delete-request?answertab=active#tab-top
       */
      if (method === 'DELETE') {
        response.data =
          status >= 200 && status < 300
            ? {
                code: 0,
                message: 'success',
                data: {},
                success: true,
              }
            : {
                code: -1,
                message: 'error',
                data: null,
                success: false,
              }
      }

      return response
    },
    (rejection) => {
      // 过滤主动取消的错误
      if (isCancelOrAbsorted(rejection)) {
        return Promise.reject(createRejectedReason('请求已取消', rejection))
      }

      if (!rejection || rejection?.response?.status === 0) {
        return Promise.reject(createRejectedReason('无法连接网络，请检查网络设置', rejection))
      }

      if (rejection instanceof Error && !Object.prototype.hasOwnProperty.call(rejection, 'response')) {
        return Promise.reject(rejection)
      }

      if (rejection.toJSON().message === 'Network Error') {
        return Promise.reject(createRejectedReason('网络连接异常', rejection))
      }

      if (rejection.toJSON().message?.includes('timeout of')) {
        return Promise.reject(createRejectedReason('网络连接超时，请重试', rejection))
      }

      const {
        status,
        config,
        data: response,
      } = rejection?.response || {
        status: undefined,
        config: undefined,
        data: undefined,
      }

      const method = (config?.method || '').toUpperCase()

      // 无法发出请求
      if (status === 0) {
        const reason = createRejectedReason('无法发出请求，请检查头部信息', rejection)
        return Promise.reject(reason)
      }

      // 资源不存在
      if (status === 404) {
        if (['POST', 'PUT', 'DELETE'].indexOf(method) !== -1) {
          const reason = createRejectedReason('无法提交数据，请确认来源正确', rejection)
          return Promise.reject(reason)
        }

        const message = response.msg || response.message
        const reason = createRejectedReason(message || '请求资源错误', rejection)
        return Promise.reject(reason)
      }

      // 资源已经被删除
      if (status === 410) {
        const message = response.msg || response.message
        const reason = createRejectedReason(message || '当前资源已经被移除', rejection)
        return Promise.reject(reason)
      }

      if (status === 433) {
        const message = response.msg || response.message
        const reason = createRejectedReason(message || '服务器繁忙，请稍后重试', rejection)
        return Promise.reject(reason)
      }

      // 服务器异常
      if (status >= 500 && status < 600) {
        const message = response.msg || response.message
        const reason = createRejectedReason(message || '服务器繁忙', rejection)
        return Promise.reject(reason)
      }
      const message = response.msg || response.message
      const reason = createRejectedReason(message || '网络错误', rejection)
      return Promise.reject(reason)
    }
  )

  return request
}

/**
 * 创建不同的 HTTP
 * @param options 域名配置
 * @param config axios 默认配置
 *
 * @description
 * 因为域名, 环境等原因额外创建一个基于 Axios
 * 扩展的方法;
 * 根据不同的业务对其进行添加或修改
 *
 * * (注意) 这里不能 Axios 原有功能进行修改,
 * * 以防跟官方文档不一样造成学习成本;
 * * 但可以扩展方法方便调用
 *
 * @description
 * 类型 Domains 主要用于传值给 ThisType，外部不需要使用该值
 * 同时 ThisType 需要用于自身，因为 ts 无法获取自身，
 * 所以这里使用 Domains | Types.HttpDomainConfigs<Domains>
 */
export function create<Domains extends Record<string, Types.HttpDomainConfig<any>>>(
  domains: Domains | Types.HttpDomainConfigs<Domains>,
  config?: AxiosRequestConfig
): Types.HttpMultipleInstances<Domains> {
  const Requestors = createAxios(config) as Types.HttpMultipleInstances<Domains>
  let env = (process.env.APP_ENV || 'product') as Types.HttpEnvironment

  const protocol = '//'
  const environments: Types.HttpEnvironment[] = ['develop', 'test', 'preview', 'product']
  if (environments.indexOf(env) === -1) {
    env = 'product'
  }
  ;(Object.keys(domains) as Array<keyof Domains>).forEach((name) => {
    const item = domains[name]
    const { interceptors, extensions } = item
    const host = item[env] || ''
    const baseURL = /^https:?\/\//.test(host) ? host : `${protocol}${host}`
    const requestor = createAxios({ ...config, baseURL })
    Requestors[name] = requestor as any

    if (typeof interceptors === 'function') {
      interceptors(requestor.interceptors)
    }

    if (typeof extensions !== 'undefined') {
      ;(Object.keys(extensions) as Array<keyof typeof extensions>).forEach((ename) => {
        const fn = (extensions[ename] as any).bind(requestor)
        if (typeof fn === 'function') {
          Object.assign(requestor, { [ename]: fn })
        }
      })
    }
  })

  return Requestors
}
